import { DateTime } from "luxon";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Breadcrumb } from "../components/Breadcrumbs";
import { Comments } from "../components/Comments";
import { Flex } from "../components/Flex";
import { Loader } from "../components/Loader";
import { Paragraph } from "../components/Paragraph";
import Topbar from "../components/Topbar";
import { useIndividual } from "../data/useIndividual";
import { ROUTE } from "../routes";
import { Image } from "../components/Image";
import { Button } from "../components/Button";
import commentsIcon from "../assets/comments-icon.svg";
import { Card } from "../components/Card";
import { RecordSharing } from "../components/RecordAccess";
import { Box } from "../components/Box";
import axios from "axios";
import theme from "../theme";
import { showError } from "../utils/error-handling";
import { notify, clearAll } from "../utils/notify";
import { useDeleteIndividual } from "../data/useDeleteIndividual";
import { useAuth } from "../context/AuthContext";
import downloadIcon from "../assets/download-arrow.svg";
import { Modal } from "../components/Modal";
import { FaTrash } from "react-icons/fa";
import { IconButtonDropdown } from "../components/IconButtonDropdown";
import { IconButtonDropdownOption } from "../components/IconButtonDropdown/IconButtonDropdownOption";
import { env } from "../utils/env";
import {
  MandateStatus,
  useMandateStatuses,
} from "../context/MandateStatusContext";
import { useMandate } from "../data/useMandate";
import { useEntity } from "../data/useEntity";
import { ProfileRecordWidget } from "../components/ProfileRecordWidget";
import { SubjectSummaryCard } from "../components/SubjectSummaryCard";
import { CheckTabStrip } from "./IndividualView/CheckTabStrip";
import { CheckDetailPanel } from "./IndividualView/CheckDetailPanel";
import { useSelectedCheck } from "./IndividualView/useSelectedCheck";

const Container = styled.div`
  ${({ theme: { space } }) => `
    padding: ${space[4]};
    height: calc(100vh - 215px);
    overflow-y: auto;
    padding-bottom: ${space[5]}
  `}
`;

const formatDate = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : undefined;

/** A single comma-joined line, wrapping rather than truncating. */
const AddressValue = ({ address }: { address: any }) => {
  if (!address) return undefined;

  const parts = [
    address.flat_appartment_subbuilding,
    address.building_number,
    address.building_name,
    address.road_street,
    address.town_city,
    address.district,
    address.post_zip_code,
    address.country_name,
  ].filter(Boolean);

  // an inline img inside one Paragraph, not a sibling flex item: a flex row
  // wraps whole items, which pushed the flag onto its own line the moment the
  // address text needed more than one line to itself
  return (
    <Paragraph mt={0} mb={0} fontSize={1}>
      {address.country_flag && (
        <img
          src={`data:image/png;base64,${address.country_flag}`}
          alt=""
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            verticalAlign: "middle",
            marginRight: "6px",
          }}
        />
      )}
      {parts.join(", ")}
    </Paragraph>
  );
};

export const CompanyChecksView = () => {
  const params = useParams();
  const { authHeader, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isRequestingPdfDownload, setIsRequestingPdfDownload] = useState(false);

  const { data: individualData, isLoading } = useIndividual(params.id);

  const individual = individualData?.data;

  const { getMandateStatusId } = useMandateStatuses();

  const { data: mandateData } = useMandate(individual?.mandate);
  const mandate = mandateData?.data;

  const { mutate: deleteScreeningRecord } = useDeleteIndividual();
  const { data: EntityDetails } = useEntity(params.id);
  const companyData = EntityDetails?.data;

  const { selectedId, selectCheck } = useSelectedCheck(companyData?.all_checks);
  const selectedCheck = companyData?.all_checks?.find(
    (check: any) => check.id === selectedId
  );

  const canDelete =
    mandate?.status?.id !== getMandateStatusId(MandateStatus.Rejected);

  const handleDownloadPdfClick = () => {
    if (isRequestingPdfDownload) {
      return;
    }

    setIsRequestingPdfDownload(true);
    notify("Generating PDF, this could take up to 30 seconds", {
      position: "bottom-left",
      type: "info",
      pauseOnFocusLoss: false,
      pauseOnHover: false,
      autoClose: false,
    });

    axios
      .get(`${env.verifyServiceUrl}individual/download_pdf/${params.id}`, {
        headers: {
          Authorization: authHeader,
        },
        responseType: "arraybuffer",
        skipGlobalErrorHandler: true,
      })
      .then((response) => {
        clearAll();
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const timestamp = DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss");
        link.download = `Customer Report - ${companyData?.reference} - ${companyData?.company_name} - ${timestamp}.pdf`;

        link.click();
      })
      .catch(() => {
        clearAll();
        setTimeout(() => {
          notify("There was a problem while generating the PDF report", {
            position: "bottom-left",
            type: "error",
          });
        }, 100);
      })
      .finally(() => {
        setIsRequestingPdfDownload(false);
      });
  };

  const handleConfirmDeleteCompanyRecord = () => {
    setIsConfirmDeleteOpen(false);

    deleteScreeningRecord(
      { id: params.id },
      {
        onSuccess: () => {
          navigate(`/screening`);
          notify("Screening record removed", {
            position: "bottom-left",
            type: "success",
          });
        },
        onError: (err: any) => {
          showError(
            err,
            "A problem occurred while deleting the screening record."
          );
        },
      }
    );
  };

  return (
    <div>
      <Topbar
        header={
          <Flex justifyContent="space-between" width="100%">
            <>
              <Breadcrumb>
                <Breadcrumb.Section linkTo={ROUTE.CHECKS}>
                  Screening
                </Breadcrumb.Section>

                <Breadcrumb.Section>
                  {companyData?.company_name}
                </Breadcrumb.Section>
              </Breadcrumb>
              <Flex justifyContent="space-between">
                {/* A screening record is a "Checks" mandate — its Division/Team
                    tagging lives on, and is saved to, the mandate. */}
                <Box mr={1}>
                  <RecordSharing
                    module="screening"
                    recordId={mandate?.id}
                    recordName={companyData?.company_name ?? "-"}
                    recordReference={companyData?.reference}
                    noun="screening record"
                    primaryDivision={mandate?.primary_division}
                    primaryTeam={mandate?.primary_team}
                    additionalDivisions={mandate?.additional_divisions}
                    additionalTeams={mandate?.additional_teams}
                    invalidateKeys={[
                      ["mandate", mandate?.id],
                      ["individual", params.id],
                      ["entity", params.id],
                    ]}
                  />
                </Box>
                <Button
                  onClick={() => setIsCommentsOpen(!isCommentsOpen)}
                  mr={1}
                  variant="secondary"
                >
                  <Image
                    src={commentsIcon}
                    alt="comments"
                    height="auto"
                    width="22px"
                    mr={1}
                  />
                  Comments
                </Button>

                {individual?.can_download_pdf && (
                  <Button
                    onClick={handleDownloadPdfClick}
                    isDisabled={
                      isRequestingPdfDownload ||
                      !hasPermission("screening.downloadpdf")
                    }
                    title={
                      hasPermission("screening.downloadpdf")
                        ? undefined
                        : "You don't have permission to download this check as a PDF"
                    }
                    variant="secondary"
                    color={theme.colors.gray[80]}
                    mr={1}
                  >
                    <Image
                      src={downloadIcon}
                      alt="download pdf"
                      height="auto"
                      width="22px"
                      mr={1}
                    />
                    <Paragraph fontWeight="500" fontSize={theme.fontSizes[1]}>
                      {isRequestingPdfDownload
                        ? "Downloading..."
                        : "Download PDF"}
                    </Paragraph>
                  </Button>
                )}

                {canDelete && (
                  <IconButtonDropdown placement="bottom-end">
                    <IconButtonDropdownOption
                      onClick={() => setIsConfirmDeleteOpen(true)}
                      IconType={FaTrash}
                      isDestructive
                    >
                      Delete
                    </IconButtonDropdownOption>
                  </IconButtonDropdown>
                )}
              </Flex>
            </>
          </Flex>
        }
      ></Topbar>

      {isLoading && (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      )}

      {!isLoading && (
        <>
          <Container>
            <Flex gap="24" alignItems="stretch" mb={4} flexWrap="wrap">
              <SubjectSummaryCard
                name={companyData?.company_name}
                chipLabel="Entity"
                identityFacts={[
                  companyData?.country?.name
                    ? companyData.country.name
                    : null,
                  companyData?.date_of_incorporation
                    ? `Incorporated ${formatDate(
                        companyData.date_of_incorporation
                      )}`
                    : null,
                  companyData?.entity_type?.name
                    ? companyData.entity_type.name
                    : null,
                  companyData?.registration_number
                    ? `No. ${companyData.registration_number}`
                    : null,
                ]}
                countryFlag={companyData?.country?.icon}
                fields={[
                  { label: "Reference", value: companyData?.reference },
                  {
                    label: "Your reference",
                    value: companyData?.external_reference,
                  },
                  {
                    label: "Address",
                    value: <AddressValue address={companyData?.address} />,
                  },
                  {
                    label: "Screening completed",
                    value: formatDate(companyData?.checks_completed_date),
                  },
                  {
                    label: "Delete date",
                    value: formatDate(companyData?.delete_date),
                    tooltip:
                      "This screening record will be deleted automatically on this date as part of our data retention policy.",
                  },
                ]}
              />

              <Card mb={0} p={3} width="300px" flexShrink={0}>
                <ProfileRecordWidget
                  record={individual}
                  recordId={individual?.id}
                  recordType="screening"
                  subjectType="entity"
                />
              </Card>
            </Flex>

            <CheckTabStrip
              checks={companyData?.all_checks ?? []}
              selectedId={selectedId}
              onSelect={selectCheck}
            />

            <CheckDetailPanel check={selectedCheck} type="entity" />
          </Container>
          <Comments
            isOpen={isCommentsOpen}
            onRequestClose={() => setIsCommentsOpen(false)}
            referenceId={companyData?.id}
            commentType="Individual"
          />
          <Modal
            isOpen={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onModalConfirmClick={handleConfirmDeleteCompanyRecord}
            title="Do you want to delete screening record?"
            content="This is an irreversible action, if you delete it you will not be able to undo the change. "
            hasSecondaryButton
            secondaryButtonText="Cancel"
            width="450px"
            primaryButtonText="Delete"
            primaryButtonVariant="danger"
          />
        </>
      )}
    </div>
  );
};
